import { computed, ComputedRef } from 'vue'

import { useStoreContacts } from '@/entities/contact'
import { isExpectedCallAsyncRejection, useWebRTC } from '@/entities/webrtc'

import { useLocalization } from '@/shared/composables/localization'
import { Contact } from '@/shared/types/api'
import { getOfferOptions } from '@/shared/utils/jssip'
import { optionalPush } from '@/shared/utils/router'
import { getChatRoute } from '@/shared/utils/url'

interface Result {
  audioCall: () => Promise<boolean>
  videoCall: () => Promise<boolean>
  callMobile: () => Promise<boolean>
  callMobileVideo: () => Promise<boolean>
  /** @deprecated /`WUI-4611. */
  callMobileInternal: () => Promise<boolean>
    /** @deprecated /`WUI-4611`. */
  callMobileInternalVideo: () => Promise<boolean>
  navToChat: () => void
  removeFromFavorite: () => Promise<void>
  toggleFavorite: () => Promise<void>
  isFavorite: ComputedRef<boolean>
}

export function useContact (contact: Contact): Result {
  const webrtc = useWebRTC()
  const storeContacts = useStoreContacts()
  const { t } = useLocalization()

  const makeCall = async (target: string, video: boolean): Promise<boolean> => {
    const options = await getOfferOptions(true, video)
    try {
      await webrtc.callAsync(target, options)
      return true
    } catch (e: unknown) {
      if (isExpectedCallAsyncRejection(e)) {
        return false
      }
      throw e
    }
  }

  const audioCall = async (): Promise<boolean> => {
    return makeCall(contact.internalNumber, false)
  }

  const videoCall = async (): Promise<boolean> => {
    return makeCall(contact.internalNumber, true)
  }

  /** @deprecated Используйте `callMobile`/`callMobileVideo`. WUI-4611 */
  const callMobile = async (): Promise<boolean> => {
    if (!contact.mobilePhone) {
      return false
    }
    return makeCall(contact.mobilePhone, false)
  }

  /** @deprecated Используйте `callMobileVideo`. WUI-4611*/
  const callMobileInternal = async (): Promise<boolean> => {
    if (!contact.internalNumber) {
      return false
    }
    return makeCall(contact.internalNumber, false)
  }

  const callMobileVideo = async (): Promise<boolean> => {
    if (!contact.mobilePhone) {
      return false
    }
    return makeCall(contact.mobilePhone, true)
  }

  const callMobileInternalVideo = async (): Promise<boolean> => {
    if (!contact.internalNumber) {
      return false
    }
    return makeCall(contact.internalNumber, true)
  }

  const navToChat = () => optionalPush(getChatRoute(contact))

  const toggleFavorite = async () => {
    try {
      return await storeContacts.setFavorite(contact, !contact.isFavorite)
    } catch {
      throw new Error(t('common.failedToChangeFavoriteOfContact'))
    }
  }

  const removeFromFavorite = async () => {
    await storeContacts.setFavorite(contact, false)
  }

  const isFavorite = computed(() => {
    return contact?.isFavorite ?? false
  })

  return {
    audioCall,
    videoCall,
    callMobile,
    callMobileVideo,
    callMobileInternal,
    callMobileInternalVideo,
    navToChat,
    toggleFavorite,
    removeFromFavorite,
    isFavorite,
  }
}
